/**
 * Adapter-Klasse zur Transformation von GA4 DataLayer Events in die alte newDataLayer Struktur
 *
 * Diese Klasse wandelt das neue GA4 Event-Format in das alte Format um:
 * - GA4: { event: "select_item", ecommerce: { items: [...], ...params } }
 * - Legacy: { event: "select_item", products: [...], attributes: {...} }
 */
export default class GA4ToLegacyAdapter {
    /**
     * Transformiert ein GA4 Event-Objekt in die alte newDataLayer Struktur
     * @param {Object} ga4Event - GA4 Event-Objekt oder Daten-Update
     * @returns {Object} Event-Objekt im alten newDataLayer Format
     */
    static transform(ga4Event) {
        if (!ga4Event || typeof ga4Event !== 'object') {
            return ga4Event;
        }

        const { event, ecommerce, ...otherParams } = ga4Event;

        // Wenn kein Event-Name vorhanden, Daten-Update durchreichen
        if (!event) {
            return ga4Event;
        }

        // Wenn es ein E-Commerce Event mit ecommerce-Struktur ist
        if (ecommerce && typeof ecommerce === 'object') {
            const { items, ...ecommerceParams } = ecommerce;

            // Transformiere items zu products
            const products = items ? this._transformItems(items) : undefined;

            // Kombiniere ecommerce params mit otherParams als attributes
            const attributes = {
                ...this._transformParams(ecommerceParams),
                ...this._transformParams(otherParams)
            };

            const legacyEvent = {
                event
            };

            // Füge products hinzu wenn vorhanden
            if (products && products.length > 0) {
                legacyEvent.products = products;
            }

            // Füge attributes hinzu wenn vorhanden
            if (Object.keys(attributes).length > 0) {
                legacyEvent.attributes = attributes;
            }

            return legacyEvent;
        }

        // Für nicht-ecommerce Events:
        // - "click" und "form" Events: Parameter direkt auf Top-Level (OHNE attributes wrapper)
        // - Alle anderen Events: Parameter in attributes wrapper
        const transformedParams = this._transformParams(otherParams);

        const legacyEvent = {
            event
        };

        // Spezialfall: "click" und "form" Events → Parameter direkt auf Top-Level
        if (event === 'click' || event === 'form') {
            Object.assign(legacyEvent, transformedParams);
        } else {
            // Alle anderen Events: attributes wrapper
            if (Object.keys(transformedParams).length > 0) {
                legacyEvent.attributes = transformedParams;
            }
        }

        return legacyEvent;
    }

    /**
     * Transformiert Parameter-Objekt von GA4 zu Legacy Format
     * Legacy Format verwendet ebenfalls snake_case (Underscores)
     * @param {Object} params - Parameter-Objekt
     * @returns {Object} Transformierte Parameter
     * @private
     */
    static _transformParams(params) {
        if (!params || typeof params !== 'object') {
            return {};
        }

        const transformed = {};

        Object.entries(params).forEach(([key, value]) => {
            const transformedKey = this._transformParamKey(key);
            transformed[transformedKey] = value;
        });

        return transformed;
    }

    /**
     * Transformiert einen Parameter-Namen von GA4 zu Legacy Format
     * Legacy Format verwendet ebenfalls Underscores (snake_case)
     * @param {string} key - Parameter-Name
     * @returns {string} Transformierter Parameter-Name
     * @private
     */
    static _transformParamKey(key) {
        // Mapping für spezifische GA4 → Legacy Umbenennungen
        const paramMapping = {
            'item_list_id': 'component',  // GA4 item_list_id → Legacy component
            'item_list_name': 'list'       // GA4 item_list_name → Legacy list
        };

        return paramMapping[key] || key;
    }

    /**
     * Transformiert ein Items-Array von GA4 zu Legacy Products Format
     * @param {Array<Object>} items - GA4 Items-Array
     * @returns {Array<Object>} Products-Array im Legacy Format
     * @private
     */
    static _transformItems(items) {
        if (!Array.isArray(items)) {
            return items;
        }

        return items.map(item => this._transformItem(item));
    }

    /**
     * Transformiert ein einzelnes Item von GA4 zu Legacy Product Format
     * @param {Object} item - GA4 Item-Objekt
     * @returns {Object} Product-Objekt im Legacy Format
     * @private
     */
    static _transformItem(item) {
        const product = {};

        Object.entries(item).forEach(([key, value]) => {
            // Transformiere item_id → id
            if (key === 'item_id') {
                product.id = value;
            }
            // Transformiere item_name → name
            else if (key === 'item_name') {
                product.name = value;
            }
            // Kombiniere item_category, item_category2-5 → category
            else if (key === 'item_category') {
                product.category = value;
            }
            else if (key.match(/^item_category[2-5]$/)) {
                // Füge weitere Kategorien mit "/" getrennt hinzu
                product.category = product.category ? `${product.category}/${value}` : value;
            }
            // Transformiere index → position
            else if (key === 'index') {
                product.position = String(value);
            }
            // Transformiere item_list_id → component
            else if (key === 'item_list_id') {
                product.component = value;
            }
            // Transformiere item_list_name → list
            else if (key === 'item_list_name') {
                product.list = value;
            }
            // Alle anderen Properties direkt übernehmen
            else if (!key.startsWith('item_')) {
                product[key] = value;
            }
        });

        return product;
    }
}
